import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { Lock, User, Shield, ArrowLeft, Loader2 } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('credentials'); // 'credentials' | '2fa'
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const codeInputs = useRef([]);

  const handleCodeChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const newCode = [...code];
    newCode[idx] = val.slice(-1);
    setCode(newCode);
    if (val && idx < 5) codeInputs.current[idx + 1]?.focus();
  };
  const handleCodeKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) codeInputs.current[idx - 1]?.focus();
  };
  const handleCodePaste = (e) => {
    const p = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!p) return;
    e.preventDefault();
    const nc = ['', '', '', '', '', ''];
    for (let i = 0; i < p.length; i++) nc[i] = p[i];
    setCode(nc);
    codeInputs.current[Math.min(p.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await adminApi.login({ ...credentials });
      if (data.requires_2fa) {
        setStage('2fa');
        setTimeout(() => codeInputs.current[0]?.focus(), 200);
      } else if (data.access_token) {
        // Primary auth: JWT in localStorage (works across proxies/browsers).
        // Bonus: backend also set an httpOnly cookie for XSS defense where
        // the browser accepts cross-origin cookies.
        localStorage.setItem('adminToken', data.access_token);
        navigate('/admin/dashboard');
      } else {
        setError('Beklenmeyen yanıt');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Kullanıcı adı veya şifre hatalı');
    } finally { setLoading(false); }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    const totp = code.join('');
    if (totp.length !== 6) { setError('6 haneli kod girin'); return; }
    setLoading(true); setError('');
    try {
      const data = await adminApi.login({ ...credentials, totp_code: totp });
      if (data.access_token) {
        // Primary auth: real JWT in localStorage (cross-browser safe).
        localStorage.setItem('adminToken', data.access_token);
        navigate('/admin/dashboard');
      } else {
        setError('Giriş yapılamadı');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Kod hatalı');
      setCode(['', '', '', '', '', '']);
      codeInputs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 font-bold text-2xl transform -skew-x-12 mb-4">
            <span className="inline-block transform skew-x-12">⚡ Cep Premium</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mt-4">
            {stage === 'credentials' ? 'Admin Girişi' : 'İki Aşamalı Doğrulama'}
          </h2>
          <p className="text-gray-600 mt-2">
            {stage === 'credentials' ? 'Yönetim paneline hoş geldiniz' : 'Authenticator uygulamanızdaki 6 haneli kodu girin'}
          </p>
        </div>

        {error && (
          <div data-testid="admin-login-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {stage === 'credentials' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Adı</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input type="text" required value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  data-testid="admin-login-username"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                  placeholder="admin" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input type="password" required value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  data-testid="admin-login-password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                  placeholder="••••••••" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? 'Kontrol ediliyor...' : 'Giriş Yap'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify2FA}>
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                <Shield className="text-green-600" size={32} />
              </div>
            </div>
            <div className="flex gap-2 justify-center mb-6" onPaste={handleCodePaste}>
              {code.map((d, i) => (
                <input key={i}
                  ref={(el) => (codeInputs.current[i] = el)}
                  type="text" inputMode="numeric" maxLength={1}
                  value={d}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  data-testid={`admin-2fa-code-${i}`}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                />
              ))}
            </div>
            <button type="submit" disabled={loading || code.join('').length !== 6}
              data-testid="admin-2fa-verify-btn"
              className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 mb-3">
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
            </button>
            <button type="button" onClick={() => { setStage('credentials'); setError(''); setCode(['','','','','','']); }}
              className="w-full text-sm text-gray-600 hover:text-red-600 flex items-center justify-center gap-1">
              <ArrowLeft size={14} /> Geri dön
            </button>
          </form>
        )}

        {stage === 'credentials' && (
          <p className="text-center text-sm text-gray-500 mt-6">
            Güvenli admin paneli · İki aşamalı doğrulama (2FA) Profil & Güvenlik sayfasından aktif edilebilir
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
